import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { openAiChat } from '@euler/app/flows/ai-suite/openAiChat';
import {
  aiColors,
  aiSuiteToolForFeature,
} from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { FeatureNotEnabledView } from '@euler/components/entitlements/FeatureNotEnabledView';
import { apdmFunnelSteps } from '@euler/functions/apdm/campaignFunnel';
import { canRedeemApdmCoupons } from '@euler/functions/apdmAccess';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AiHomeFeature } from '@euler/model/ai-home';
import {
  ApdmDashboardPeriod,
  ApdmDashboardResult,
  kApdmRunningCampaignStatuses,
} from '@euler/model/apdm';
import { StoreFeature } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import {
  CommonActions,
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import dayjs from 'dayjs';
import {
  ChevronLeft,
  ChevronRight,
  CirclePlus,
  ScanLine,
} from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ApdmFunnelBars } from '../components/ApdmFunnelChart';
import { ApdmCard, ApdmSectionTitle } from '../components/ApdmSurface';
import { DashboardCampaignCard } from './DashboardCampaignCard';
import {
  DashboardEmpty,
  DashboardFailed,
  DashboardLoading,
} from './DashboardEmpty';
import { DashboardStats } from './DashboardStats';
import { DashboardTodo } from './DashboardTodo';

const kPeriods: Array<{ value: ApdmDashboardPeriod; label: string }> = [
  { value: ApdmDashboardPeriod.Week, label: '近7天' },
  { value: ApdmDashboardPeriod.Month, label: '近30天' },
  { value: ApdmDashboardPeriod.All, label: '全部' },
];

const kWideFrom = 768;

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flexDirection: 'row',
        padding: rpx(3),
        borderRadius: rpx(10),
        backgroundColor: aiColors.muted,
      }}
    >
      {options.map(option => {
        const active = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(option.value)}
            style={{
              paddingHorizontal: rpx(12),
              paddingVertical: rpx(6),
              borderRadius: rpx(8),
              backgroundColor: active ? aiColors.card : 'transparent',
              shadowColor: '#0F172A',
              shadowOpacity: active ? 0.08 : 0,
              shadowRadius: 3,
              shadowOffset: { width: 0, height: 1 },
            }}
          >
            <Typography.Text
              medium
              size={rpx(12)}
              color={active ? aiColors.foreground : aiColors.mutedForeground}
            >
              {option.label}
            </Typography.Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function periodRange(dashboard: ApdmDashboardResult | null): string {
  if (!dashboard) return '';
  if (!dashboard.from) return '全部时间';
  const format = (value: string) => dayjs(value).format('M月D日');
  return `${format(dashboard.from)} – ${format(dashboard.to)}`;
}

function ApdmDashboard() {
  const { rpx } = useResponsiveContext();
  const canRedeem = canRedeemApdmCoupons(useUserProfileInfo() ?? null);
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const wide = width >= kWideFrom;
  const navigation = useAppNavigation();
  const local = useNavigation();
  const { apdmCampaignService } = useServiceFactory();
  const [period, setPeriod] = useState(ApdmDashboardPeriod.Month);
  const [dashboard, setDashboard] = useState<ApdmDashboardResult | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [tab, setTab] = useState<'running' | 'ended'>('running');
  const [undelivered, setUndelivered] = useState(false);

  // Read again on every return: a campaign paused or a reply reviewed on
  // another page changes these numbers.
  const load = useCallback(() => {
    let active = true;
    setFailed(false);
    apdmCampaignService
      .dashboard(period)
      .then(result => {
        if (active) setDashboard(result);
      })
      .catch(error => {
        console.error('APDM dashboard failed', error);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [apdmCampaignService, period]);
  useFocusEffect(load);
  // A reload after an action on this page, when the page keeps its focus.
  useEffect(() => {
    if (reload) return load();
  }, [load, reload]);

  const campaigns = useMemo(() => {
    const all = dashboard?.campaigns ?? [];
    return {
      running: all.filter(item =>
        kApdmRunningCampaignStatuses.has(item.summary.status),
      ),
      ended: all.filter(
        item => !kApdmRunningCampaignStatuses.has(item.summary.status),
      ),
    };
  }, [dashboard]);
  const shown = campaigns[tab];
  const empty =
    dashboard != null &&
    dashboard.campaigns.length === 0 &&
    !dashboard.todo.pendingReviews;

  const back = () => {
    if (local.canGoBack()) local.goBack();
    else navigation.navigate('Main', {});
  };
  const create = () =>
    openAiChat(navigation, {
      kind: 'new',
      tool: aiSuiteToolForFeature(AiHomeFeature.Apdm),
    });
  const openCampaign = (campaignId: string, filter?: 'undelivered') => {
    setUndelivered(false);
    navigation.navigate('ApdmCampaign', { campaignId, filter });
  };
  const openTab = (screen: 'Inbox' | 'Leads') =>
    local.dispatch(
      CommonActions.navigate('Main', { screen: 'Tabs', params: { screen } }),
    );

  const content = {
    width: '100%',
    maxWidth: 1120,
    alignSelf: 'center',
  } as const;
  return (
    <View style={{ flex: 1, backgroundColor: aiColors.background }}>
      <View
        style={{
          paddingTop: insets.top + rpx(10),
          paddingBottom: rpx(12),
          paddingHorizontal: rpx(16),
          backgroundColor: aiColors.card,
          borderBottomWidth: 1,
          borderBottomColor: aiColors.border,
        }}
      >
        <View style={[content, { gap: rpx(12) }]}>
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="返回"
              onPress={back}
              hitSlop={8}
              style={{
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronLeft size={20} color={aiColors.foreground} />
            </Pressable>
            <Typography.Text
              bold
              size={rpx(16)}
              color={aiColors.foreground}
              style={{ flex: 1 }}
            >
              营销看板
            </Typography.Text>
            {canRedeem ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => navigation.navigate('ApdmCouponRedemption', {})}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(6),
                  paddingHorizontal: rpx(12),
                  paddingVertical: rpx(8),
                  borderRadius: rpx(10),
                  borderWidth: 1,
                  borderColor: aiColors.border,
                  backgroundColor: aiColors.card,
                  opacity: pressed ? 0.85 : 1,
                })}
              >
                <ScanLine size={16} color={aiColors.foreground} />
                <Typography.Text
                  medium
                  size={rpx(12)}
                  color={aiColors.foreground}
                >
                  核销
                </Typography.Text>
              </Pressable>
            ) : null}
            <Pressable
              accessibilityRole="button"
              onPress={create}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(6),
                paddingHorizontal: rpx(12),
                paddingVertical: rpx(8),
                borderRadius: rpx(10),
                backgroundColor: aiColors.primary,
                opacity: pressed ? 0.85 : 1,
              })}
            >
              <CirclePlus size={16} color="#fff" />
              <Typography.Text medium size={rpx(12)} color="#fff">
                新建活动
              </Typography.Text>
            </Pressable>
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: rpx(12),
            }}
          >
            <Segmented options={kPeriods} value={period} onChange={setPeriod} />
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {periodRange(dashboard)}
            </Typography.Text>
          </View>
        </View>
      </View>
      {!dashboard ? (
        failed ? (
          <DashboardFailed onRetry={() => setReload(value => value + 1)} />
        ) : (
          <DashboardLoading />
        )
      ) : (
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            padding: rpx(16),
            paddingBottom: insets.bottom + rpx(32),
          }}
        >
          <View style={[content, { flexGrow: 1, gap: rpx(20) }]}>
            {empty ? (
              <DashboardEmpty
                title={
                  period === ApdmDashboardPeriod.All
                    ? '还没有营销活动'
                    : '这段时间没有营销活动'
                }
                onCreate={create}
              />
            ) : (
              <>
                {dashboard.scope === 'assigned' ? (
                  <Typography.Text
                    size={rpx(12)}
                    color={aiColors.mutedForeground}
                  >
                    仅显示分配给我的客户
                  </Typography.Text>
                ) : null}
                <DashboardStats
                  dashboard={dashboard}
                  running={campaigns.running.length}
                  wide={wide}
                />
                <View
                  style={{
                    flexDirection: wide ? 'row' : 'column',
                    alignItems: wide ? 'flex-start' : 'stretch',
                    gap: rpx(20),
                  }}
                >
                  <View style={{ flex: wide ? 1 : undefined }}>
                    <DashboardTodo
                      todo={dashboard.todo}
                      onReviews={() => openTab('Inbox')}
                      onLeads={() => openTab('Leads')}
                      onUndelivered={() =>
                        dashboard.todo.undelivered.length === 1
                          ? openCampaign(
                              dashboard.todo.undelivered[0].campaignId,
                              'undelivered',
                            )
                          : setUndelivered(true)
                      }
                    />
                  </View>
                  <ApdmCard
                    style={{
                      flex: wide ? 1 : undefined,
                      padding: rpx(16),
                      gap: rpx(16),
                    }}
                  >
                    <ApdmSectionTitle
                      title="整体转化"
                      // The listed campaigns' own totals, not just what
                      // happened inside the period.
                      note={
                        period === ApdmDashboardPeriod.All
                          ? '全部活动'
                          : `进行中和${kPeriods.find(
                              item => item.value === period,
                            )?.label}结束的活动`
                      }
                    />
                    <ApdmFunnelBars
                      steps={apdmFunnelSteps(dashboard.funnel)}
                      basis="previous"
                    />
                  </ApdmCard>
                </View>
                <View style={{ gap: rpx(12) }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography.Text
                      medium
                      size={rpx(14)}
                      color={aiColors.foreground}
                    >
                      活动
                    </Typography.Text>
                    <Segmented
                      options={[
                        {
                          value: 'running' as const,
                          label: `进行中 ${campaigns.running.length}`,
                        },
                        {
                          value: 'ended' as const,
                          label: `已结束 ${campaigns.ended.length}`,
                        },
                      ]}
                      value={tab}
                      onChange={setTab}
                    />
                  </View>
                  {shown.length ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: rpx(12),
                      }}
                    >
                      {shown.map(item => (
                        <View
                          key={item.summary.id}
                          style={{
                            width: wide
                              ? width >= 1100
                                ? '32%'
                                : '48.5%'
                              : '100%',
                          }}
                        >
                          <DashboardCampaignCard
                            item={item}
                            onPress={() => openCampaign(item.summary.id)}
                          />
                        </View>
                      ))}
                    </View>
                  ) : (
                    <Typography.Text
                      size={rpx(13)}
                      color={aiColors.mutedForeground}
                      style={{ paddingVertical: rpx(12) }}
                    >
                      {tab === 'running'
                        ? '现在没有进行中的活动'
                        : '这段时间没有结束的活动'}
                    </Typography.Text>
                  )}
                </View>
              </>
            )}
          </View>
        </ScrollView>
      )}
      <BottomPanel
        visible={undelivered}
        onClose={() => setUndelivered(false)}
        fitContent
      >
        <View style={{ padding: rpx(16), gap: rpx(4) }}>
          <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
            没发出去的客户
          </Typography.Text>
          {dashboard?.todo.undelivered.map(item => (
            <Pressable
              key={item.campaignId}
              accessibilityRole="button"
              onPress={() => openCampaign(item.campaignId, 'undelivered')}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: rpx(12),
                borderBottomWidth: 1,
                borderBottomColor: aiColors.border,
              }}
            >
              <Typography.Text
                size={rpx(14)}
                color={aiColors.foreground}
                style={{ flex: 1 }}
              >
                {item.title}
              </Typography.Text>
              <Typography.Text size={rpx(13)} color={aiColors.destructive}>
                {item.count} 位
              </Typography.Text>
              <ChevronRight size={16} color={aiColors.mutedForeground} />
            </Pressable>
          ))}
        </View>
      </BottomPanel>
    </View>
  );
}

export const ApdmDashboardPage = wrapNavigatorScreen(
  () => {
    const profile = useUserProfileInfo();
    const apdm = useStoreFeature(StoreFeature.Apdm);
    if (!apdm.enabled)
      return <FeatureNotEnabledView feature={StoreFeature.Apdm} />;
    if (!profile?.user.enabled)
      return <Text>当前门店尚未开通私域营销，或账号不可用。</Text>;
    return <ApdmDashboard key={`${profile.org.id}:${profile.store.id}`} />;
  },
  { title: '营销看板', headerShown: false, headerMode: 'float' },
);
